import { useMemo, useState } from 'react';
import { ArrowLeft, X } from 'lucide-react';
import { getEtatQuiz, matiereKey } from '../../../utils/quizEleveStatus';
import { useTheme } from '../../../hooks/fetch_api_hook/useTheme';
import { useClasseContext } from '../../../context/ClasseContext';
import { useXp } from '../../../context/XpContext';
import { attendusJouables, defisCreditesAffiches, defisTotalAffiche, defisEpuises } from '../../../utils/defisQuotidiens';
import { DEFIS_QUOTA_JOUR } from '../../../utils/xpConfig';
import { ILE_PATH, ILE_SCALE, ILE_CENTRE, REGIONS, DECOR, ancrePct } from './ileShape';
import VolcanRegion from './VolcanRegion';
import ForetBrume from './ForetBrume';
import VolcanBrume from './VolcanBrume';

/**
 * « L'Île des Défis » — vue ARCHIPEL de l'espace élève (La Réunion).
 * PRÉSENTATIONNELLE : reçoit les mêmes props que QuizSelectionViewEleve et
 * réutilise le routage de clic existant (onStartQuiz / onStartSession /
 * onShowCorrection / onStartDefi) + le helper getEtatQuiz.
 *
 * UNE seule île (La Réunion, chez Tano le tuit-tuit), navigation à 2 niveaux :
 *  - EN MER : fanions-mois = quiz donnés par les profs (toutes matières,
 *    badge 🔢/📖 par quiz dans la fiche). Tap → fiche des quiz du mois.
 *  - SUR L'ÎLE : deux PORTES DE RÉGION — « le Volcan des Nombres » (maths,
 *    Piton de la Fournaise) et « la Forêt des Mots » (français, Roche
 *    Écrite). Tap → EXPÉRIENCE IMMERSIVE plein écran : VolcanRegion
 *    (2 scènes, portails = thèmes, énergie = quota défis) ou ForetBrume
 *    (scène d'attente tant que les générateurs français n'existent pas).
 *  - LE NID DE TANO (teaser des œufs — mécanique future).
 * Aucune donnée / aucun moteur de quiz touché.
 */

const COULEUR_FANION = {
  afaire: '#F97316',     // orange — au moins un quiz à faire
  termine: '#1F9D55',    // vert — tout terminé
  verrouille: '#9FB6CC', // gris — injouable
  bientot: '#9FB6CC',    // gris — pas encore dispo
};

const MOIS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];

// Les deux régions narratives de l'île (ancres géographiques dans ileShape).
const REGIONS_META = [
  { key: 'volcan', matiere: 'maths', nom: 'Le Volcan des Nombres', emoji: '🌋', teinte: '#F97316' },
  { key: 'foret', matiere: 'francais', nom: 'La Forêt des Mots', emoji: '🌳', teinte: '#1F9D55' },
];

const EMOJI_MATIERE = { maths: '🔢', francais: '📖' };
const ORDRE_MATIERES = ['maths', 'francais'];
const matDe = (q) => (matiereKey(q.matiere) === 'francais' ? 'francais' : 'maths');

// Icône « nid de Tano » en SVG inline — l'emoji 🪺 (Unicode 14) ne s'affiche
// pas sur Windows 10 et une partie des appareils élèves.
const NidIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" aria-hidden="true" style={{ display: 'block' }}>
    {/* œuf qui dépasse du nid */}
    <ellipse cx="16" cy="13" rx="6" ry="7.5" fill="#FFF3D6" stroke="#E5C98F" strokeWidth="1" />
    {/* coupe du nid */}
    <path d="M3,16 Q16,30 29,16 Q26,24 16,25.5 Q6,24 3,16 Z" fill="#A9743E" stroke="#7C4A03" strokeWidth="1" strokeLinejoin="round" />
    {/* brindilles */}
    <path d="M4,17 Q16,23.5 28,17" stroke="#7C4A03" strokeWidth="1.2" fill="none" strokeLinecap="round" />
    <path d="M7,20.5 Q16,25 25,20.5" stroke="#8A5F33" strokeWidth="1" fill="none" strokeLinecap="round" />
  </svg>
);

const dateQuiz = (q) => new Date(q.source === 'session' ? q.date_passation : q.date);

// Clé / libellé du mois d'un quiz (regroupement des fanions par mois).
const moisDe = (q) => {
  const d = dateQuiz(q);
  if (isNaN(d.getTime())) return { key: 'sans-date', label: 'Sans date', order: Number.MAX_SAFE_INTEGER };
  const nom = MOIS[d.getMonth()];
  return {
    key: `${d.getFullYear()}-${d.getMonth()}`,
    label: nom.charAt(0).toUpperCase() + nom.slice(1),
    order: d.getFullYear() * 12 + d.getMonth(),
  };
};

// État agrégé d'un mois (priorité à faire > bientôt > terminé > verrouillé).
const etatMois = (quizz, verifier) => {
  const etats = quizz.map((q) => getEtatQuiz(q, verifier).etat);
  if (etats.includes('afaire')) return 'afaire';
  if (etats.includes('bientot')) return 'bientot';
  if (etats.length && etats.every((e) => e === 'termine')) return 'termine';
  return 'verrouille';
};

// Fanion d'un MOIS — gros marqueur qui flotte EN MER (bouée + mât + banderole animée).
// `duJour` : le fanion du quiz le plus récent à faire → halo pulsant + GO! animé.
function FanionMois({ etat, label, count, position, onClick, duJour = false }) {
  const couleur = COULEUR_FANION[etat] || '#9FB6CC';
  const fort = etat === 'afaire';
  return (
    <button
      type="button"
      onClick={onClick}
      title={`${label} — ${count} quiz`}
      aria-label={`${label}, ${count} quiz`}
      className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300 rounded"
      style={{ left: `${position.left}%`, top: `${position.top}%`, opacity: etat === 'termine' ? 0.82 : 1 }}
    >
      <span className="relative">
        <svg width="46" height="54" viewBox="0 0 48 56" style={{ display: 'block', filter: 'drop-shadow(0 3px 3px rgba(0,0,0,.3))', overflow: 'visible' }}>
          {/* Halo pulsant sous la bouée (fanion du jour) */}
          {duJour && (
            <ellipse className="ile-flag-halo" cx="16" cy="49" rx="15" ry="6" fill="rgba(249,115,22,.45)" />
          )}
          <ellipse cx="16" cy="52.5" rx="11" ry="2.6" fill="rgba(0,0,0,.22)" />
          <ellipse cx="16" cy="47" rx="9.5" ry="6" fill="#0E2A4D" stroke="#fff" strokeWidth="1.6" />
          <ellipse cx="16" cy="45.4" rx="6.4" ry="3.1" fill={couleur} opacity="0.5" />
          <rect x="14.3" y="8" width="3.4" height="38" rx="1.7" fill="#0A1F3D" />
          <circle cx="16" cy="7" r="2.6" fill="#0A1F3D" />
          <g className={`eleve-flag-cloth${fort || duJour ? ' eleve-flag-cloth--strong' : ''}`}>
            <path d="M17.7,9 L43,12.5 L36,18 L43,23.5 L17.7,20.5 Z" fill={couleur} stroke="#fff" strokeWidth="1.8" strokeLinejoin="round" />
          </g>
        </svg>
        {/* Badge nombre de quiz du mois */}
        <span
          className="absolute -bottom-1 -right-1 min-w-[18px] h-[18px] px-1 text-[10px] font-extrabold text-white rounded-full flex items-center justify-center"
          style={{ background: '#2D7FF9', border: '2px solid #fff' }}
        >
          {count}
        </span>
        {fort && (
          <span
            className={`absolute -top-1 right-0 text-[9px] font-extrabold text-white px-1.5 py-px rounded-full shadow${duJour ? ' eleve-cta' : ''}`}
            style={{ background: 'linear-gradient(120deg,#F97316,#FFB938)' }}
          >
            GO!
          </span>
        )}
      </span>
      <span
        className="max-w-[7rem] truncate text-[11px] font-extrabold px-2 py-0.5 rounded-full -mt-1 shadow"
        style={{ background: 'rgba(255,255,255,.95)', color: '#0A1F3D' }}
      >
        {label}
      </span>
    </button>
  );
}

// Porte de RÉGION sur l'île — l'entrée vers les défis d'une matière.
// `brume` : aucun thème jouable (pas de générateurs, ex. français) → pastille
// mystère sous brume dérivante ; sinon pastille colorée de la région.
// `appel` : il reste des défis à gagner aujourd'hui → surbrillance pulsante
// + badge ⚡ pour inciter au clic (retombe à 5/5 et au repos).
function PorteRegion({ meta, position, brume, repos, appel = false, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={brume ? `${meta.nom} — cette région dort encore…` : meta.nom}
      aria-label={meta.nom}
      className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-300 rounded transition-transform active:scale-95"
      style={{ left: `${position.left}%`, top: `${position.top}%` }}
    >
      <span className="relative">
        <span
          className={`rounded-full flex items-center justify-center text-[19px]${appel ? ' ile-defi-pulse' : ''}`}
          style={{
            width: 42, height: 42,
            background: brume
              ? 'radial-gradient(circle at 32% 26%, #1b3f68, #0E2A4D)'
              : repos
                ? 'radial-gradient(circle at 32% 26%, #1b3f68, #0E2A4D)'
                : `radial-gradient(circle at 32% 26%, rgba(255,255,255,.25), transparent 60%), ${meta.teinte}`,
            border: brume ? '2px solid rgba(41,182,232,.55)' : '2.5px solid #fff',
            boxShadow: brume ? '0 3px 10px rgba(10,31,61,.5)' : `0 3px 12px ${meta.teinte}66`,
          }}
        >
          {brume ? '❔' : repos ? '💤' : meta.emoji}
        </span>
        {/* Badge ⚡ d'appel au clic (des défis restent à gagner) */}
        {appel && (
          <span
            className="eleve-cta absolute -top-1.5 -right-1.5 w-[19px] h-[19px] rounded-full flex items-center justify-center text-[11px]"
            style={{ background: 'linear-gradient(120deg,#F97316,#FFB938)', border: '2px solid #fff' }}
            aria-hidden="true"
          >
            ⚡
          </span>
        )}
        {/* Brume dérivante sur la porte (région pas encore éveillée) */}
        {brume && (
          <svg className="absolute pointer-events-none" width="56" height="28" viewBox="0 0 56 28" style={{ left: -7, top: 18 }} aria-hidden="true">
            <ellipse className="ile-mist" cx="20" cy="14" rx="17" ry="7" fill="rgba(255,255,255,.30)" style={{ filter: 'blur(2px)' }} />
            <ellipse className="ile-mist" cx="38" cy="17" rx="14" ry="6" fill="rgba(255,255,255,.22)" style={{ filter: 'blur(2px)', animationDelay: '1.6s' }} />
          </svg>
        )}
      </span>
      <span
        className="max-w-[7.5rem] text-center leading-tight text-[10px] font-extrabold px-2 py-0.5 rounded-lg mt-1 shadow"
        style={{ background: 'rgba(255,255,255,.95)', color: '#0A1F3D' }}
      >
        {meta.nom}{brume ? ' ✨' : ''}
      </span>
    </button>
  );
}

// peutRejouer (quiz → bool) et onStartRejeu : « Rejouer » un quiz en ligne (décision du fondateur, 04/10).
const IleEleve = ({ allQuizz, verifierSiEleveARepondu, onBack, onStartQuiz, onStartSession, onShowCorrection, onStartDefi, peutRejouer, onStartRejeu }) => {
  // Regroupement PAR MOIS → un fanion par mois (toutes matières confondues).
  const moisBuckets = useMemo(() => {
    const map = new Map();
    (allQuizz || []).forEach((q) => {
      const { key, label, order } = moisDe(q);
      if (!map.has(key)) map.set(key, { key, label, order, quizz: [] });
      map.get(key).quizz.push(q);
    });
    return [...map.values()].sort((a, b) => a.order - b.order);
  }, [allQuizz]);

  // Positions des fanions-mois EN MER (couronne au large de l'île — l'espace
  // vient surtout d'ILE_SCALE réduit dans ileShape : l'île est plus petite,
  // la mer plus grande, les fanions ne la chevauchent plus).
  const bucketPos = useMemo(() => {
    const n = moisBuckets.length;
    return moisBuckets.map((_, i) => {
      const angle = -Math.PI / 2 + (i * 2 * Math.PI) / Math.max(n, 1);
      return { left: 50 + 42 * Math.cos(angle), top: 50 + 36 * Math.sin(angle) };
    });
  }, [moisBuckets]);

  // « Fanion du jour » = le mois (le plus récent) qui contient encore un quiz à faire.
  const bucketDuJour = useMemo(() => {
    const candidats = moisBuckets.filter(b => etatMois(b.quizz, verifierSiEleveARepondu) === 'afaire');
    if (candidats.length === 0) return null;
    return candidats.reduce((max, b) => (b.order > max.order ? b : max), candidats[0]).key;
  }, [moisBuckets, verifierSiEleveARepondu]);

  // ── Thèmes du niveau, répartis par RÉGION (matière) ──
  const { classeNom } = useClasseContext();
  const niveau = ((classeNom || (typeof localStorage !== 'undefined' && localStorage.getItem('classeNom')) || '')[0]) || '';
  const { data: themesData } = useTheme(niveau || null);

  const regions = useMemo(() => {
    const list = Array.isArray(themesData) ? themesData : [];
    const exposeMatiere = list.some((t) => t && t.matiere !== undefined);
    return REGIONS_META.map((meta) => {
      // Sans champ matiere (vieux back) : tout va au Volcan (maths) — la
      // Forêt reste sous brume plutôt que d'afficher des thèmes mélangés.
      const themes = exposeMatiere
        ? list.filter((t) => t && matiereKey(t.matiere) === meta.matiere)
        : (meta.matiere === 'maths' ? list : []);
      const themesJouables = themes.filter((t) => attendusJouables(t).length > 0);
      return { ...meta, themes, themesJouables };
    });
  }, [themesData]);

  // ── Défis quotidiens : compteur x/5 + plafond 30 (vérité serveur, repli local) ──
  const { profil } = useXp();
  const defisCredites = defisCreditesAffiches(profil);
  const quotaAtteint = defisCredites >= DEFIS_QUOTA_JOUR;
  const reposJour = defisEpuises(profil);
  const defisTotal = defisTotalAffiche(profil);
  const aDesDefis = regions.some((r) => r.themesJouables.length > 0);

  const compte = useMemo(() => {
    const total = (allQuizz || []).length;
    const termines = (allQuizz || []).filter((e) => verifierSiEleveARepondu(e)).length;
    return { termines, aFaire: total - termines };
  }, [allQuizz, verifierSiEleveARepondu]);

  // ── Fiches au tap : quiz du mois / nid — et navigation vers les RÉGIONS ──
  const [moisOuvert, setMoisOuvert] = useState(null);
  const [matiereFiche, setMatiereFiche] = useState('maths'); // onglet matière de la fiche mois
  // Région ouverte = une CLÉ (pas l'objet) : les thèmes restent frais quand
  // le fetch arrive. Restaurée depuis sessionStorage au RETOUR D'UN DÉFI
  // (lancer un défi démonte l'île — VolcanRegion pose le flag au lancement
  // pour que l'élève revienne DANS le cratère voir sa pierre s'allumer).
  const [regionCle, setRegionCle] = useState(() => {
    try {
      const v = sessionStorage.getItem('tanoIleRetourRegion');
      sessionStorage.removeItem('tanoIleRetourRegion');
      return v || null;
    } catch { return null; }
  });
  const [nidOuvert, setNidOuvert] = useState(false);

  // Ouvre la fiche d'un mois sur l'onglet le plus utile : la matière du quiz
  // « à faire » le plus récent, sinon celle du premier quiz du mois.
  const ouvrirMois = (b) => {
    const aFaire = b.quizz.filter((q) => getEtatQuiz(q, verifierSiEleveARepondu).etat === 'afaire');
    const ref = aFaire.length
      ? aFaire.reduce((max, q) => (dateQuiz(q) > dateQuiz(max) ? q : max), aFaire[0])
      : b.quizz[0];
    setMatiereFiche(ref ? matDe(ref) : 'maths');
    setMoisOuvert(b);
  };

  // Action disponible pour un quiz (routage IDENTIQUE à QuizSelectionViewEleve).
  const actionQuiz = (q) => {
    const { isSession, isCompleted, isAvailable, verrouille } = getEtatQuiz(q, verifierSiEleveARepondu);
    if (!isAvailable) return { kind: 'disabled', label: 'Bientôt' };
    if (verrouille) return { kind: 'disabled', label: 'Indisponible' };
    if (isCompleted) {
      if (q.correction_visible) return { kind: 'review', label: 'Voir la correction', run: () => onShowCorrection(q) };
      return { kind: 'disabled', label: 'Correction bientôt' };
    }
    return { kind: 'play', label: 'Jouer', run: () => (isSession ? onStartSession(q) : onStartQuiz(q)) };
  };

  const metaQuiz = (q) => {
    const d = dateQuiz(q);
    const dateStr = isNaN(d.getTime()) ? '' : d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
    const src = q.source === 'session' ? 'En ligne' : (q.groupe ? 'Groupe' : 'Classe');
    return [src, dateStr].filter(Boolean).join(' · ');
  };

  const ileVide = moisBuckets.length === 0 && !regions.some((r) => r.themes.length > 0);

  // ── Navigation niveau 2 : l'expérience immersive de la région ouverte ──
  const regionActive = regionCle ? regions.find((r) => r.key === regionCle) : null;
  if (regionActive) {
    if (regionActive.key === 'volcan') {
      // Volcan sans thème jouable (élève hors 6e tant que seuls les
      // automatismes 6e ont des générateurs) → scène d'attente, comme la
      // Forêt — jamais un cratère aux portails tous « Bientôt ».
      return regionActive.themesJouables.length > 0 ? (
        <VolcanRegion
          region={regionActive}
          onStartDefi={onStartDefi}
          onBack={() => setRegionCle(null)}
        />
      ) : (
        <VolcanBrume region={regionActive} onBack={() => setRegionCle(null)} />
      );
    }
    return <ForetBrume region={regionActive} onBack={() => setRegionCle(null)} />;
  }

  return (
    <div
      className="eleve-gamified min-h-screen p-5 pb-24 relative overflow-hidden"
      style={{ background: 'linear-gradient(180deg,#0A1F3D,#122E54 62%,#0A1F3D)' }}
    >
      {/* Header : retour */}
      <div className="flex items-center justify-between gap-3 mb-4 max-w-2xl mx-auto">
        <button onClick={onBack} aria-label="Retour" className="p-2.5 rounded-xl text-white" style={{ background: 'rgba(255,255,255,.1)' }}>
          <ArrowLeft className="w-5 h-5" />
        </button>
        <span aria-hidden="true" />
      </div>

      {/* Titre */}
      <div className="text-center mb-3 max-w-2xl mx-auto">
        <h1 className="text-white text-2xl">L'Île des Défis</h1>
        <p className="text-sm font-bold" style={{ color: 'rgba(255,255,255,.65)' }}>
          {aDesDefis
            ? 'Les fanions en mer = les quiz de tes profs. Sur l\'île : les régions de tes défis !'
            : 'Chaque fanion en mer regroupe les quiz de tes profs d\'un mois. Touche un fanion pour les ouvrir.'}
        </p>
      </div>

      {/* Bandeau défis du jour. À 5/5 : VERT « validé » + phrase « sans XP ».
          À 30 défis joués (plafond bien-être) : message VICTOIRE + repos. */}
      {aDesDefis && (
        <div className="flex flex-col items-center gap-1 mb-3 max-w-2xl mx-auto">
          {reposJour ? (
            <>
              <div
                className="flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-extrabold"
                style={{ background: 'rgba(255,185,56,.18)', color: '#FFD27A', border: '1px solid rgba(255,185,56,.5)' }}
              >
                <span aria-hidden="true">🔥</span>
                <span>Waouh, {defisTotal} défis aujourd'hui !!</span>
              </div>
              <p className="text-[11px] font-bold" style={{ color: 'rgba(255,255,255,.6)' }}>
                Tu as déjà bien bossé — repose-toi et reviens demain ! 💤
              </p>
            </>
          ) : (
            <>
              <div
                className="flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-extrabold"
                style={quotaAtteint
                  ? { background: 'rgba(31,157,85,.2)', color: '#7EE2A8', border: '1px solid rgba(31,157,85,.55)' }
                  : { background: 'rgba(249,115,22,.18)', color: '#FFB938', border: '1px solid rgba(249,115,22,.5)' }}
              >
                <span aria-hidden="true">{quotaAtteint ? '✅' : '⚡'}</span>
                <span>Défis du jour : {Math.min(defisCredites, DEFIS_QUOTA_JOUR)}/{DEFIS_QUOTA_JOUR}</span>
              </div>
              {quotaAtteint && (
                <p className="text-[11px] font-bold" style={{ color: 'rgba(255,255,255,.6)' }}>
                  Bravo, tout est gagné ! Tu peux continuer à t'entraîner — sans XP aujourd'hui.
                </p>
              )}
            </>
          )}
        </div>
      )}

      {/* Océan + La Réunion + couches */}
      {ileVide ? (
        <div className="text-center py-16">
          <div className="text-5xl mb-3">🏝️</div>
          <p className="text-lg" style={{ color: 'rgba(255,255,255,.7)' }}>
            Rien sur ton île pour l'instant — reviens bientôt !
          </p>
        </div>
      ) : (
        <div className="relative mx-auto w-full max-w-xl" style={{ aspectRatio: '160 / 120' }}>
          {/* Mer + île (réduite pour laisser une marge de mer) + décor géographique */}
          <svg viewBox="0 0 160 120" className="w-full h-full block absolute inset-0">
            <ellipse cx="80" cy="62" rx="78" ry="56" fill="#1A4574" opacity="0.32" />
            <g transform={`translate(${ILE_CENTRE.x} ${ILE_CENTRE.y}) scale(${ILE_SCALE}) translate(${-ILE_CENTRE.x} ${-ILE_CENTRE.y})`}>
              <path d={ILE_PATH} fill="#EAF3FB" stroke="#29B6E8" strokeWidth="1.6" strokeLinejoin="round" />
              {/* Les 3 cirques (Mafate, Salazie, Cilaos) */}
              {DECOR.cirques.map((c, i) => (
                <ellipse key={i} cx={c.cx} cy={c.cy} rx={c.rx} ry={c.ry} fill="#29B6E8" opacity="0.09" />
              ))}
              {/* Cône du Piton de la Fournaise */}
              <path d={DECOR.volcanCone} fill="#0E2A4D" opacity="0.14" />
              <ellipse cx={DECOR.volcanCratere.cx} cy={DECOR.volcanCratere.cy} rx={DECOR.volcanCratere.rx} ry={DECOR.volcanCratere.ry} fill="#F97316" opacity="0.5" />
            </g>
          </svg>

          {/* Portes de région (Forêt des Mots / Volcan des Nombres) */}
          {regions.map((r) => (
            <PorteRegion
              key={r.key}
              meta={r}
              position={ancrePct(REGIONS[r.key])}
              brume={r.themesJouables.length === 0}
              repos={reposJour}
              appel={r.themesJouables.length > 0 && !quotaAtteint && !reposJour}
              onClick={() => setRegionCle(r.key)}
            />
          ))}

          {/* Le nid de Tano (teaser des œufs — mécanique future) */}
          <button
            type="button"
            onClick={() => setNidOuvert(true)}
            title="Le nid de Tano"
            aria-label="Le nid de Tano"
            className="absolute -translate-x-1/2 -translate-y-1/2 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300 rounded-full transition-transform active:scale-95"
            style={{ left: `${ancrePct(REGIONS.nid).left}%`, top: `${ancrePct(REGIONS.nid).top}%` }}
          >
            <span
              className="rounded-full flex items-center justify-center"
              style={{ width: 28, height: 28, background: 'rgba(10,31,61,.55)', border: '1.5px solid rgba(255,185,56,.55)' }}
            >
              <NidIcon size={17} />
            </span>
          </button>

          {/* Fanions-mois (quiz profs) en mer — celui « du jour » est mis en
              avant ; l'étiquette annonce les matières du mois (🔢/📖). */}
          {moisBuckets.map((b, i) => {
            const emojis = ORDRE_MATIERES
              .filter((m) => b.quizz.some((q) => matDe(q) === m))
              .map((m) => EMOJI_MATIERE[m])
              .join('');
            return (
              <FanionMois
                key={b.key}
                etat={etatMois(b.quizz, verifierSiEleveARepondu)}
                label={`${b.label} ${emojis}`}
                count={b.quizz.length}
                position={bucketPos[i]}
                onClick={() => ouvrirMois(b)}
                duJour={b.key === bucketDuJour}
              />
            );
          })}
        </div>
      )}

      {/* Compteur (quiz profs, toutes matières) */}
      {(allQuizz || []).length > 0 && (
        <p className="text-center text-sm font-bold mt-4" style={{ color: 'rgba(255,255,255,.6)' }}>
          {compte.aFaire} à faire · {compte.termines} terminé{compte.termines > 1 ? 's' : ''}
        </p>
      )}

      {/* ── Fiche au tap : quiz du mois ── */}
      {moisOuvert && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(10,31,61,.82)' }}
          onClick={(e) => { if (e.target === e.currentTarget) setMoisOuvert(null); }}
        >
          <div className="relative w-full max-w-[420px] rounded-3xl p-4 pt-5" style={{ background: '#fff', border: '1px solid #C9DFF0', boxShadow: '0 20px 60px rgba(0,0,0,.5)' }}>
            <button
              onClick={() => setMoisOuvert(null)}
              aria-label="Fermer"
              className="absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center"
              style={{ background: '#EAF3FB', color: '#0A1F3D' }}
            >
              <X className="w-4 h-4" />
            </button>

            <div className="mb-3">
              <div className="text-[17px] font-extrabold" style={{ color: '#0A1F3D' }}>
                🚩 {moisOuvert.label}
              </div>
              <div className="text-[12px] font-bold" style={{ color: '#4A5A6E' }}>
                {moisOuvert.quizz.length} quiz
              </div>
            </div>

            {/* Chips de matière — seulement si le mois est bi-matière. Point
                orange sur l'onglet qui contient encore des « à faire » :
                l'élève voit que l'autre matière l'attend, même sans scroller. */}
            {(() => {
              const matieresMois = ORDRE_MATIERES.filter((m) => moisOuvert.quizz.some((q) => matDe(q) === m));
              const biMatiere = matieresMois.length > 1;
              const prio = (q) => {
                const e = getEtatQuiz(q, verifierSiEleveARepondu).etat;
                return e === 'afaire' ? 0 : e === 'bientot' ? 1 : 2;
              };
              const liste = (biMatiere ? moisOuvert.quizz.filter((q) => matDe(q) === matiereFiche) : moisOuvert.quizz)
                .slice()
                .sort((a, b) => prio(a) - prio(b));
              return (
                <>
                  {biMatiere && (
                    <div className="flex gap-1 p-1 rounded-full mb-3" style={{ background: '#EAF3FB' }}>
                      {matieresMois.map((m) => {
                        const quizM = moisOuvert.quizz.filter((q) => matDe(q) === m);
                        const nbAFaire = quizM.filter((q) => getEtatQuiz(q, verifierSiEleveARepondu).etat === 'afaire').length;
                        const actif = matiereFiche === m;
                        return (
                          <button
                            key={m}
                            type="button"
                            onClick={() => setMatiereFiche(m)}
                            className="relative flex-1 px-3 py-1.5 rounded-full text-[13px] font-bold transition-colors"
                            style={actif
                              ? { background: 'linear-gradient(120deg,#29B6E8,#2D7FF9)', color: '#fff' }
                              : { color: '#4A5A6E' }}
                          >
                            {EMOJI_MATIERE[m]} {m === 'francais' ? 'Français' : 'Maths'} ({quizM.length})
                            {nbAFaire > 0 && (
                              <span
                                className="absolute top-0.5 right-1.5 w-2.5 h-2.5 rounded-full"
                                style={{ background: '#F97316', border: '2px solid #fff' }}
                                aria-label={`${nbAFaire} à faire`}
                              />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  <div className="space-y-2 max-h-[60vh] overflow-y-auto">
                    {liste.map((q, i) => {
                      const a = actionQuiz(q);
                      const disabled = a.kind === 'disabled';
                      const emojiMatiere = EMOJI_MATIERE[matDe(q)];
                      return (
                  // title : le titre entier en info-bulle, la ligne le coupe (demande du prof, 2026-10-04).
                  <div key={`${q.source}-${q.id || i}`} title={q.titre} className="flex items-center gap-3 p-2.5 rounded-2xl" style={{ background: '#F6FAFE' }}>
                    <div
                      className="w-9 h-9 rounded-xl flex items-center justify-center text-lg flex-shrink-0"
                      style={{ background: a.kind === 'play' ? 'linear-gradient(120deg,#F97316,#FFB938)' : '#9FB6CC' }}
                    >
                      {emojiMatiere}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[14px] font-bold truncate" style={{ color: '#0A1F3D' }}>{q.titre}</div>
                      <div className="text-[11px] font-bold" style={{ color: '#4A5A6E' }}>{metaQuiz(q)}</div>
                    </div>
                    <button
                      type="button"
                      disabled={disabled}
                      onClick={() => { if (a.run) { a.run(); setMoisOuvert(null); } }}
                      className="text-[13px] font-bold px-3.5 py-2 rounded-xl flex-shrink-0"
                      style={disabled
                        ? { background: '#E3EAF1', color: '#8FA0B2', cursor: 'not-allowed' }
                        : a.kind === 'review'
                          ? { background: '#EAF3FB', color: '#0A1F3D', borderBottom: '3px solid #C9DFF0' }
                          : { background: '#2D7FF9', color: '#fff', borderBottom: '3px solid #1E5FC4' }}
                    >
                      {a.label}
                    </button>
                    {/* Rejouer : dès la copie rendue ; grisé une fois les défis
                        crédités du jour faits (rejeux compris). */}
                    {peutRejouer && onStartRejeu && peutRejouer(q) ? (
                      <button
                        type="button"
                        disabled={quotaAtteint}
                        title={quotaAtteint ? `Tu as fait tes ${DEFIS_QUOTA_JOUR} défis du jour` : undefined}
                        onClick={() => { onStartRejeu(q); setMoisOuvert(null); }}
                        className="text-[13px] font-bold px-3.5 py-2 rounded-xl flex-shrink-0"
                        style={quotaAtteint
                          ? { background: '#E3EAF1', color: '#8FA0B2', cursor: 'not-allowed' }
                          : { background: '#2D7FF9', color: '#fff', borderBottom: '3px solid #1E5FC4' }}
                      >
                        Rejouer
                      </button>
                    ) : null}
                  </div>
                      );
                    })}
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* ── Fiche au tap : le nid de Tano (teaser des œufs) ── */}
      {nidOuvert && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(10,31,61,.82)' }}
          onClick={(e) => { if (e.target === e.currentTarget) setNidOuvert(false); }}
        >
          <div className="relative w-full max-w-[340px] rounded-3xl p-5 pt-6 text-center" style={{ background: '#fff', border: '1px solid #C9DFF0', boxShadow: '0 20px 60px rgba(0,0,0,.5)' }}>
            <button
              onClick={() => setNidOuvert(false)}
              aria-label="Fermer"
              className="absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center"
              style={{ background: '#EAF3FB', color: '#0A1F3D' }}
            >
              <X className="w-4 h-4" />
            </button>
            <div className="flex justify-center mb-2"><NidIcon size={52} /></div>
            <div className="text-[17px] font-extrabold" style={{ color: '#0A1F3D', fontFamily: 'Fredoka' }}>
              Le nid de Tano
            </div>
            <p className="mt-2 text-[13px] font-bold" style={{ color: '#4A5A6E' }}>
              Quelque chose se prépare ici, tout en haut de la Roche Écrite…
              Continue de gagner de l'XP pour le découvrir ✨
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default IleEleve;
